'use client';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Eye, EyeOff, Loader2, ChevronRight, ChevronLeft, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { accountService } from '@/lib/banking/bankingService';

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  dob: string;
  ssn: string;
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  accountType: 'checking' | 'savings' | 'both';
  password: string;
  confirmPassword: string;
  agreeTerms: boolean;
}

const steps = ['Personal Info', 'Address & ID', 'Account Setup'];

export default function SignUpForm({ onSuccess }: { onSuccess: () => void }) {
  const [step, setStep] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const { signUp } = useAuth();
  const router = useRouter();

  const {
    register,
    handleSubmit,
    watch,
    trigger,
    formState: { errors },
  } = useForm<SignUpFormData>({
    defaultValues: { accountType: 'both' },
  });

  const password = watch('password');

  const handleNext = async () => {
    const stepFields: (keyof SignUpFormData)[][] = [
      ['firstName', 'lastName', 'email', 'phone', 'dob'],
      ['ssn', 'addressLine1', 'city', 'state', 'zip'],
      ['accountType', 'password', 'confirmPassword', 'agreeTerms'],
    ];
    const valid = await trigger(stepFields[step]);
    if (valid) setStep((s) => s + 1);
  };

  const onSubmit = async (data: SignUpFormData) => {
    setIsLoading(true);
    try {
      const result = await signUp(data.email, data.password, {
        fullName: `${data.firstName} ${data.lastName}`,
        firstName: data.firstName,
        lastName: data.lastName,
        role: 'customer',
      });

      if (result?.user) {
        // Create bank accounts
        await accountService.createAccountsForUser(result.user.id);
        toast.success('Account created! Welcome to Capital City BNT.');
        router.push('/customer-dashboard');
      }
    } catch (error: any) {
      toast.error(error?.message || 'Registration failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div>
      {/* Step indicator */}
      <div className="flex items-center gap-2 mb-6">
        {steps.map((s, i) => (
          <React.Fragment key={`step-${s}`}>
            <div className="flex items-center gap-1.5">
              <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-700 transition-all ${
                i < step ? 'bg-success text-white' : i === step ? 'bg-primary text-white' : 'bg-muted text-muted-foreground'
              }`}>
                {i < step ? <CheckCircle2 size={14} /> : i + 1}
              </div>
              <span className={`text-xs font-500 hidden sm:block ${i === step ? 'text-foreground' : 'text-muted-foreground'}`}>
                {s}
              </span>
            </div>
            {i < steps.length - 1 && (
              <div className={`flex-1 h-0.5 rounded-full ${i < step ? 'bg-success' : 'bg-muted'}`} />
            )}
          </React.Fragment>
        ))}
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        {/* Step 0: Personal Info */}
        {step === 0 && (
          <div className="space-y-3 fade-in">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="firstName" className="block text-sm font-600 text-foreground mb-1.5">First Name</label>
                <input id="firstName" type="text" {...register('firstName', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.firstName ? 'border-danger' : 'border-border'}`} placeholder="Marcus" />
                {errors.firstName && <p className="mt-1 text-xs text-danger">{errors.firstName.message}</p>}
              </div>
              <div>
                <label htmlFor="lastName" className="block text-sm font-600 text-foreground mb-1.5">Last Name</label>
                <input id="lastName" type="text" {...register('lastName', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.lastName ? 'border-danger' : 'border-border'}`} placeholder="Whitfield" />
                {errors.lastName && <p className="mt-1 text-xs text-danger">{errors.lastName.message}</p>}
              </div>
            </div>
            <div>
              <label htmlFor="signup-email" className="block text-sm font-600 text-foreground mb-1.5">Email Address</label>
              <input id="signup-email" type="email" {...register('email', { required: 'Required', pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' } })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.email ? 'border-danger' : 'border-border'}`} placeholder="marcus@email.com" />
              {errors.email && <p className="mt-1 text-xs text-danger">{errors.email.message}</p>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="phone" className="block text-sm font-600 text-foreground mb-1.5">Phone Number</label>
                <input id="phone" type="tel" {...register('phone', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.phone ? 'border-danger' : 'border-border'}`} placeholder="(555) 234-7890" />
                {errors.phone && <p className="mt-1 text-xs text-danger">{errors.phone.message}</p>}
              </div>
              <div>
                <label htmlFor="dob" className="block text-sm font-600 text-foreground mb-1.5">Date of Birth</label>
                <input id="dob" type="date" {...register('dob', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.dob ? 'border-danger' : 'border-border'}`} />
                {errors.dob && <p className="mt-1 text-xs text-danger">{errors.dob.message}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Address & ID */}
        {step === 1 && (
          <div className="space-y-3 fade-in">
            <div>
              <label htmlFor="ssn" className="block text-sm font-600 text-foreground mb-1.5">SSN (Last 4 digits)</label>
              <p className="text-xs text-muted-foreground mb-1.5">Used for identity verification only. Never stored in full.</p>
              <input id="ssn" type="text" maxLength={4} {...register('ssn', { required: 'Required', pattern: { value: /^\d{4}$/, message: 'Enter exactly 4 digits' } })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring ${errors.ssn ? 'border-danger' : 'border-border'}`} placeholder="XXXX" />
              {errors.ssn && <p className="mt-1 text-xs text-danger">{errors.ssn.message}</p>}
            </div>
            <div>
              <label htmlFor="addressLine1" className="block text-sm font-600 text-foreground mb-1.5">Street Address</label>
              <input id="addressLine1" type="text" {...register('addressLine1', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.addressLine1 ? 'border-danger' : 'border-border'}`} placeholder="1247 Maple Grove Drive" />
              {errors.addressLine1 && <p className="mt-1 text-xs text-danger">{errors.addressLine1.message}</p>}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-1">
                <label htmlFor="city" className="block text-sm font-600 text-foreground mb-1.5">City</label>
                <input id="city" type="text" {...register('city', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.city ? 'border-danger' : 'border-border'}`} placeholder="Austin" />
                {errors.city && <p className="mt-1 text-xs text-danger">{errors.city.message}</p>}
              </div>
              <div>
                <label htmlFor="state" className="block text-sm font-600 text-foreground mb-1.5">State</label>
                <input id="state" type="text" maxLength={2} {...register('state', { required: 'Required' })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.state ? 'border-danger' : 'border-border'}`} placeholder="TX" />
                {errors.state && <p className="mt-1 text-xs text-danger">{errors.state.message}</p>}
              </div>
              <div>
                <label htmlFor="zip" className="block text-sm font-600 text-foreground mb-1.5">ZIP Code</label>
                <input id="zip" type="text" maxLength={5} {...register('zip', { required: 'Required', pattern: { value: /^\d{5}$/, message: 'Invalid ZIP' } })} className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.zip ? 'border-danger' : 'border-border'}`} placeholder="78701" />
                {errors.zip && <p className="mt-1 text-xs text-danger">{errors.zip.message}</p>}
              </div>
            </div>
          </div>
        )}

        {/* Step 2: Account Setup */}
        {step === 2 && (
          <div className="space-y-3 fade-in">
            <div>
              <label className="block text-sm font-600 text-foreground mb-2">Account Type</label>
              <div className="grid grid-cols-3 gap-2">
                {(['checking', 'savings', 'both'] as const).map((type) => (
                  <label key={`acct-type-${type}`} className="cursor-pointer">
                    <input type="radio" value={type} {...register('accountType')} className="sr-only" />
                    <div className={`p-3 border-2 rounded-lg text-center text-xs font-600 transition-all ${
                      watch('accountType') === type ? 'border-primary bg-primary/5 text-primary' : 'border-border text-muted-foreground hover:border-muted-foreground'
                    }`}>
                      {type === 'checking' ? 'Checking' : type === 'savings' ? 'Savings' : 'Both'}
                    </div>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <label htmlFor="signup-password" className="block text-sm font-600 text-foreground mb-1.5">Create Password</label>
              <p className="text-xs text-muted-foreground mb-1.5">Minimum 8 characters with uppercase, number, and symbol</p>
              <div className="relative">
                <input
                  id="signup-password"
                  type={showPassword ? 'text' : 'password'}
                  {...register('password', {
                    required: 'Password is required',
                    minLength: { value: 8, message: 'Minimum 8 characters' },
                    pattern: { value: /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])/, message: 'Must include uppercase, number, and symbol' },
                  })}
                  className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm pr-10 focus:outline-none focus:ring-2 focus:ring-ring ${errors.password ? 'border-danger' : 'border-border'}`}
                  placeholder="Create a strong password"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.password && <p className="mt-1 text-xs text-danger">{errors.password.message}</p>}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-600 text-foreground mb-1.5">Confirm Password</label>
              <input
                id="confirmPassword"
                type="password"
                {...register('confirmPassword', {
                  required: 'Please confirm your password',
                  validate: (v) => v === password || 'Passwords do not match',
                })}
                className={`w-full px-3.5 py-2.5 bg-input border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-ring ${errors.confirmPassword ? 'border-danger' : 'border-border'}`}
                placeholder="Repeat your password"
              />
              {errors.confirmPassword && <p className="mt-1 text-xs text-danger">{errors.confirmPassword.message}</p>}
            </div>
            <div className="flex items-start gap-2 pt-1">
              <input id="agreeTerms" type="checkbox" {...register('agreeTerms', { required: 'You must agree to the terms' })} className="w-4 h-4 mt-0.5 accent-primary" />
              <label htmlFor="agreeTerms" className="text-xs text-muted-foreground leading-relaxed">
                I agree to Capital City BNT's{' '}
                <button type="button" className="text-accent underline">Terms of Service</button>
                {' '}and{' '}
                <button type="button" className="text-accent underline">Privacy Policy</button>
              </label>
            </div>
            {errors.agreeTerms && <p className="text-xs text-danger">{errors.agreeTerms.message}</p>}
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center gap-3 mt-6">
          {step > 0 && (
            <button type="button" onClick={() => setStep((s) => s - 1)} className="flex items-center gap-1.5 px-4 py-2.5 border border-border rounded-lg text-sm font-600 text-foreground hover:bg-muted transition-colors">
              <ChevronLeft size={16} />
              Back
            </button>
          )}
          {step < steps.length - 1 ? (
            <button type="button" onClick={handleNext} className="flex-1 flex items-center justify-center gap-1.5 py-2.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all">
              Continue
              <ChevronRight size={16} />
            </button>
          ) : (
            <button type="submit" disabled={isLoading} className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-primary text-primary-foreground text-sm font-600 rounded-lg hover:bg-primary/90 active:scale-95 transition-all disabled:opacity-60">
              {isLoading ? <><Loader2 size={16} className="animate-spin" /> Opening Account...</> : 'Open My Account'}
            </button>
          )}
        </div>
      </form>
    </div>
  );
}